import { inject, Injectable } from '@angular/core'
import { TranslateService } from '@ngx-translate/core'
import { lastValueFrom } from 'rxjs'
import { CallResource, CallsService } from './calls.service'
import { FileValidationService } from './file-validation.service'
import { LoggerService } from './logger.service'
import { ToastService } from './toast.service'
import { getUploadErrorMessage, UploadsService } from './uploads.service'
import { SentryService } from './sentry.service'
import { getFileExtension, getFileName, SourceDocumentFormat, toSourceDocumentFormat } from './utils'
import { MAX_CUSTOM_DOCUMENT_SIZE_IN_BYTES } from './call-document-upload/call-document-upload.component'
import { MAXIMUM_TOTAL_PAGES_FOR_CONTENT_BLOCK } from './template-editor.component/template-item-content-block/template-item-content-block.component'

const SOURCE_DOCUMENT_MIME_TO_FORMAT: Record<string, SourceDocumentFormat> = {
  'application/pdf': 'PDF',
  'application/vnd.openxmlformats-officedocument.wordprocessingml.document': 'DOCX',
  'application/vnd.openxmlformats-officedocument.presentationml.presentation': 'PPTX',
}

function resolveSourceDocumentFormat(file: File): SourceDocumentFormat {
  if (Object.hasOwn(SOURCE_DOCUMENT_MIME_TO_FORMAT, file.type)) {
    return SOURCE_DOCUMENT_MIME_TO_FORMAT[file.type]
  }
  return toSourceDocumentFormat(getFileExtension(file))
}

export interface TemplateSourceEditorTarget {
  addPendingSourceToBlock(blockId: number, fileName: string): void
  addSourceToBlock(blockId: number, resource: CallResource, fileName?: string): void
  removePendingSourceFromBlock(blockId: number, fileName: string): void
}

@Injectable({ providedIn: 'root' })
export class DocumentUploadService {
  private uploadsService = inject(UploadsService)
  private callsService = inject(CallsService)
  private toastService = inject(ToastService)
  private translateService = inject(TranslateService)
  private fileValidationService = inject(FileValidationService)
  private logger = inject(LoggerService)
  private sentryService = inject(SentryService)
  private importResourceMutation = this.callsService.importResource()

  async uploadCustomDocumentSourceToBlock(callId: number, blockId: number, file: File, editor: TemplateSourceEditorTarget): Promise<void> {
    const validationFailure = this.fileValidationService.validateFile(file, ['pdf', 'docx', 'pptx'], MAX_CUSTOM_DOCUMENT_SIZE_IN_BYTES)
    if (validationFailure !== null) {
      this.reportRejection(validationFailure, file, callId)
      return
    }
    const fileName = getFileName(file)
    const sourceDocumentFormat = resolveSourceDocumentFormat(file)
    editor.addPendingSourceToBlock(blockId, fileName)
    try {
      const uploadInfo = await lastValueFrom(this.uploadsService.uploadCustomDocument(file))
      if (uploadInfo.uploadKey === null) {
        throw new Error('Upload key not received for file upload with name ' + fileName)
      }
      const createdResource = await this.importResourceMutation.mutateAsync({
        callId,
        data: {
          uploadKey: uploadInfo.uploadKey,
          targetResourceExtension: 'PDF',
          fileType: 'CUSTOM_DOCUMENT',
          fileName,
          sourceDocumentFormat,
        },
      })
      editor.addSourceToBlock(blockId, createdResource, fileName)
    } catch (error) {
      this.logger.error('Error uploading file', error)
      editor.removePendingSourceFromBlock(blockId, fileName)
      const reason = (error as { error?: { message?: string } })?.error?.message ?? 'upload_failed'
      this.reportRejection(reason, file, callId)
      const uploadErrorMessage = getUploadErrorMessage(error)
      if (uploadErrorMessage !== null) {
        this.toastService.show(uploadErrorMessage, { maxPages: MAXIMUM_TOTAL_PAGES_FOR_CONTENT_BLOCK })
      } else {
        this.toastService.showRaw(this.translateService.instant('TEMPLATES.EDITOR.FILE_UPLOAD_FAILED'))
      }
    }
  }

  private reportRejection(reason: string, file: File, callId: number): void {
    this.sentryService.captureMessage('Content-block document upload rejected', {
      level: 'warning',
      tags: { feature: 'document-upload' },
      extra: { reason, callId, fileName: getFileName(file), fileSizeBytes: file.size },
    })
  }
}
